import React, { useEffect, useState, useCallback } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import './JobRequiredQualsPicker.css';

// Used inside FM job-create / job-edit forms.
// Controlled component — parent owns `value` (array of {qualification_id, min_count})
// and receives changes via `onChange`.
const JobRequiredQualsPicker = ({ value = [], onChange }) => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTaxonomy = useCallback(async () => {
    try {
      const r = await fetch(getApiEndpoint('/api/qualifications'), { headers: getAuthHeader() });
      if (!r.ok) return;
      const data = await r.json();
      setTaxonomy(Array.isArray(data?.qualifications) ? data.qualifications : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTaxonomy(); }, [fetchTaxonomy]);

  const addRequirement = () => {
    const used = new Set(value.map(v => v.qualification_id));
    const available = taxonomy.find(q => !used.has(q.id));
    if (!available) return;
    onChange([...value, { qualification_id: available.id, min_count: 1 }]);
  };

  const removeRequirement = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const updateRequirement = (idx, patch) => {
    onChange(value.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const lookup = (id) => taxonomy.find(q => q.id === Number(id));

  if (loading) return <div className="cs-jrqp-state">Loading qualifications…</div>;
  if (taxonomy.length === 0) {
    return (
      <div className="cs-jrqp-state cs-jrqp-empty">
        No qualifications configured yet. <a href="/admin/qualifications">Add some</a> to enable the quote gate.
      </div>
    );
  }

  return (
    <div className="cs-jrqp">
      <div className="cs-jrqp-help">
        Optional. Contractors won't be able to submit a quote unless they have staff with these qualifications.
      </div>
      {value.length === 0 && (
        <div className="cs-jrqp-empty-list">No requirements set.</div>
      )}
      {value.map((req, idx) => {
        const qual = lookup(req.qualification_id);
        return (
          <div key={idx} className="cs-jrqp-row">
            <select
              className="cs-jrqp-select"
              value={req.qualification_id}
              onChange={e => updateRequirement(idx, { qualification_id: Number(e.target.value) })}
            >
              {taxonomy.map(q => (
                <option key={q.id} value={q.id}>
                  {q.name} {q.category ? `(${q.category})` : ''}
                </option>
              ))}
            </select>
            <div className="cs-jrqp-count">
              <label>×</label>
              <input
                type="number"
                min="1"
                max="50"
                value={req.min_count}
                onChange={e => updateRequirement(idx, { min_count: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              />
            </div>
            <button
              type="button"
              className="cs-jrqp-remove"
              onClick={() => removeRequirement(idx)}
              aria-label="Remove requirement"
              title={qual ? `Remove ${qual.name}` : 'Remove'}
            >
              <FaTimes />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="cs-jrqp-add"
        onClick={addRequirement}
        disabled={value.length >= taxonomy.length}
      >
        <FaPlus /> Add requirement
      </button>
    </div>
  );
};

export default JobRequiredQualsPicker;
